import React, { useEffect, useMemo, useState } from 'react';


import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { SESSION_STATES, TimeFormats } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { useParticipantNetworkIssueAlert } from '../hooks/use-participant-network-issue-alert';
import { useChangeAppointmentState, useSessionByAppointmentIdQuery } from '@uplift/queries';
import { LegacyDateTimeService } from '@uplift/services';
import { UserRole } from '@uplift/types';
import { TwilioMeta } from '../twilio-meta';
import { Spinner, useDisabledAutoLogoutTillMounted } from '@uplift/ui-v2';

import { ProviderSidebar, ProviderSidebarProps, VideoCallLayout } from './components';
import {
  MainVideoControlPanel
} from './components/main-video-control-panel/MainVideoControlPanel.component';
import { MainParticipant } from './main-participant.component';
import { useVideoCallParticipants } from './use-video-call-participants.hook';
import { Appointment, CurrentUser } from '@uplift/api';

const useStyles = makeStyles()(() => ({
  videoCallBlock: {
    marginTop: 60,
    height: '100vh',
    width: '100vw',
    left: 0,
  },
}));

export type VideoCallPageProps = {
  me: CurrentUser;
  appointment: Appointment;
  meta?: TwilioMeta;
  onHungUp: () => void;
} & Pick<ProviderSidebarProps, 'renderPsychiatryDrawer' | 'SessionNoteComponent'>;

export const VideoCallPage: React.FC<VideoCallPageProps> = (({ me, onHungUp, appointment, renderPsychiatryDrawer, SessionNoteComponent }) => {
  const { data: session } = useSessionByAppointmentIdQuery(appointment.id, { enabled: !!appointment.id && me.role !== UserRole.patient });
  const [ isLoading, setIsLoading ] = useState(true);
  const videoCallParticipants = useVideoCallParticipants();

  useDisabledAutoLogoutTillMounted();

  const { changeAppointmentState } = useChangeAppointmentState();
  const l10n = useL10n();
  const { classes } = useStyles();

  const startTime = LegacyDateTimeService.getLocalTime(appointment?.fromDatetime, TimeFormats.LT);
  const endTime = LegacyDateTimeService.getLocalTime(appointment?.toDatetime, TimeFormats.LT);
  const sessionDetails = `${l10n.sessions.session} ${startTime} - ${endTime}`;

  useParticipantNetworkIssueAlert(me);

  const startSession = async () => {
    try {
      if (appointment.state !== SESSION_STATES.started) {
        await changeAppointmentState(
          appointment.id,
          SESSION_STATES.started
        );
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const onHangupConfirmHandler = () => {
    onHungUp();
  };

  useEffect(() => {
    startSession();
  }, []);

  const providerSidebar = useMemo(() => {
    if (me.role === UserRole.provider) {
      return (
        <ProviderSidebar
          appointmentId={appointment.id}
          sessionId={session.id}
          patientId={appointment.patientId}
          medicalRole={me.medicalRole}
          renderPsychiatryDrawer={renderPsychiatryDrawer}
          SessionNoteComponent={SessionNoteComponent}
        />
      );
    }

    return null;
  }, [ me.role, me.medicalRole, appointment, session, renderPsychiatryDrawer, SessionNoteComponent ]);

  if (isLoading) {
    return (
      <Box className={classes.videoCallBlock}>
        <Spinner />
      </Box>
    );
  }

  return (
    <VideoCallLayout
      mainParticipant={<MainParticipant sessionDetails={sessionDetails} />}
      controlPanel={(
        <MainVideoControlPanel
          sessionDetails={sessionDetails}
          hungUpHandler={() => onHangupConfirmHandler()}
          isBlurAvaibile={me?.role === UserRole.provider}
        />
      )}
      participants={videoCallParticipants}
      sidebar={providerSidebar}
    />
  );
});
